/**
 * ONE glossary for every metric column in the app (PR 9b, plan I9-1 / I9-4).
 *
 * WHY: the same word meant different things on different screens -- PPC "Sales"
 * was 7-day ad sales while the hub's "Sales" was total sales, "Units" was stock
 * on Products child rows and units sold on the hub, stock cover was "Stock" on
 * Campaigns, "Days of supply" on Products and "Days of cover" on Restock, and
 * impressions were "Imps", "Impr." and "Impressions". Now a metric has exactly
 * one label, unit, window suffix and formula tooltip, and a column spreads
 * `metricCol(id)` instead of typing its own words. The source scan
 * (columnLabels.scan.test.ts) fails a table that goes back to a literal.
 *
 * Pure, no React. The Restock glossary (ops/pages/restockHub/columnGlossary.ts,
 * PR 11) is absorbed by reference: its entries take label / unit from here
 * where the concept exists, and add the row-typed accessors that belong there.
 * (The two are joined in the tests, not here: columnGlossary imports THIS
 * module, so importing it back would be a cycle.)
 */
import type { ColumnDef } from '../components/EntityTable'

/** The one sentence that defines FBA stock on every page (Products, the product
 *  page, Restock); backend/product_facts.py FBA_STOCK_DEFINITION says the same. */
export const FBA_STOCK_DEFINITION =
  'FBA stock = available + reserved + in transfer between Amazon warehouses. Inbound is shown separately.'

export type ColumnGroup =
  | 'identity' | 'action' | 'where' | 'status' | 'money' | 'efficiency' | 'volume' | 'stock' | 'diagnostics'

/** The thin header caption of each group. Identity / action never caption. */
export const GROUP_LABEL: Record<ColumnGroup, string> = {
  identity: '',
  action: '',
  where: 'Where',
  status: 'Status',
  money: 'Money',
  efficiency: 'Efficiency',
  volume: 'Volume',
  stock: 'Stock',
  diagnostics: 'Details',
}

export type MetricUnit =
  | 'money' | 'percent' | 'int' | 'days' | 'multiple' | 'units' | 'packs' | 'cases' | 'per_day' | 'date' | 'text'

/** undefined = the table's own window (no suffix). */
export type MetricWindow = 'today' | '7d' | '30d' | '90d' | '12mo'

export interface MetricEntry {
  /** The ONE label, no window suffix, no unit noise. */
  label: string
  unit: MetricUnit
  group: ColumnGroup
  /** One plain-English line, ends with "." -- the header tooltip. */
  formula: string
  /** A worked example where there is arithmetic (plan I7). */
  example?: string
  totals: 'sum' | 'ratio' | 'none'
  /** PPC ROW fields (the row key for ad sales stays `sales`); a page may
   *  override through `metricCol` opts. */
  ratio?: { num: string; den: string | string[] }
  format?: 'money' | 'percent' | 'int'
  filter?: 'money' | 'percent' | 'number' | 'text' | 'boolean'
  /** A point-in-time value (a bid, a stock count): no "selected date range"
   *  sentence in the tooltip. */
  point?: true
}

const M = (e: MetricEntry): MetricEntry => e

export const METRICS = {
  // ── Money ────────────────────────────────────────────────────────────────
  spend: M({
    label: 'Spend', unit: 'money', group: 'money', totals: 'sum', format: 'money', filter: 'money',
    formula: 'Ad spend: what Amazon charged for clicks.',
  }),
  ad_spend: M({
    label: 'Ad spend', unit: 'money', group: 'money', totals: 'sum', format: 'money', filter: 'money',
    formula: 'All advertising spend.',
  }),
  ad_sales: M({
    label: 'Ad sales', unit: 'money', group: 'money', totals: 'sum', format: 'money', filter: 'money',
    formula: 'Sales Amazon attributes to an ad click within 7 days.',
  }),
  sales: M({
    label: 'Sales', unit: 'money', group: 'money', totals: 'sum', format: 'money', filter: 'money',
    formula: 'Total sales, organic and ad, from the P&L.',
  }),
  net_profit: M({
    label: 'Net profit', unit: 'money', group: 'money', totals: 'sum', format: 'money', filter: 'money',
    formula: 'What is left after cost of goods, marketplace fees, refunds and advertising.',
  }),
  margin: M({
    label: 'Margin', unit: 'percent', group: 'money', totals: 'ratio', format: 'percent', filter: 'percent',
    ratio: { num: 'net_profit', den: 'sales' },
    formula: 'Net profit ÷ sales.',
  }),
  acos: M({
    label: 'ACoS', unit: 'percent', group: 'money', totals: 'ratio', format: 'percent', filter: 'percent',
    ratio: { num: 'spend', den: 'sales' },
    formula: 'Ad spend ÷ ad sales.',
    example: '$25 ÷ $100 = 25 %.',
  }),
  target_acos: M({
    label: 'Target ACoS', unit: 'percent', group: 'money', totals: 'none', format: 'percent', filter: 'percent', point: true,
    formula: 'The ACoS this campaign is steered to: its rule’s target when a rule is bound, otherwise its product’s goal target.',
  }),
  tacos: M({
    label: 'TACoS', unit: 'percent', group: 'money', totals: 'none', format: 'percent', filter: 'percent',
    formula: 'Ad spend ÷ total sales (organic + ad). Always at or below ACoS; the gap is the organic share.',
    example: '$25 ÷ $250 = 10 %.',
  }),
  be_acos: M({
    label: 'Break-even ACoS', unit: 'percent', group: 'money', totals: 'none', format: 'percent', filter: 'percent', point: true,
    formula: 'The ACoS at which a sale earns nothing after cost of goods and fees.',
  }),
  orders: M({
    label: 'Orders', unit: 'int', group: 'money', totals: 'sum', format: 'int', filter: 'number',
    formula: 'Orders attributed to ads within 7 days of the click.',
  }),
  orders_all: M({
    label: 'Orders', unit: 'int', group: 'money', totals: 'sum', format: 'int', filter: 'number',
    formula: 'Every order in the window, not only the ones an ad drove.',
  }),
  // feedback #121 - the halo: what the ad's clicks sold when it was another of our items.
  other_item_sales: M({
    label: 'Other-item sales', unit: 'money', group: 'money', totals: 'sum', format: 'money', filter: 'money',
    formula: 'Sales of a different one of our items by shoppers who clicked this ad (Amazon 7-day attribution).',
  }),
  other_item_orders: M({
    label: 'Other-item orders', unit: 'int', group: 'money', totals: 'sum', format: 'int', filter: 'number',
    formula: 'Orders for a different one of our items by shoppers who clicked this ad (Amazon 7-day attribution).',
  }),
  daily_budget: M({
    label: 'Daily budget', unit: 'money', group: 'money', totals: 'none', format: 'money', filter: 'money', point: true,
    formula: 'The most the campaign may spend in one day.',
  }),
  budget_use: M({
    label: 'Budget used', unit: 'percent', group: 'money', totals: 'ratio', format: 'percent', filter: 'percent',
    ratio: { num: 'spend', den: 'budget_capacity' },
    formula: 'Spend ÷ (daily budget × days in the window). 100 % = the budget was spent every day.',
    example: '$150 ÷ ($10 × 30 days) = 50 %.',
  }),
  budget_capacity: M({
    label: 'Budget capacity', unit: 'money', group: 'money', totals: 'sum', format: 'money', filter: 'money',
    formula: 'Daily budget × days in the window: the most the campaign could have spent.',
  }),
  pacing: M({
    label: 'Pacing', unit: 'percent', group: 'status', totals: 'none', format: 'percent', point: true,
    formula: 'Today’s spend ÷ daily budget, live from Amazon.',
  }),
  bid: M({
    label: 'Bid', unit: 'money', group: 'money', totals: 'none', format: 'money', filter: 'money', point: true,
    formula: 'What you bid per click.',
  }),
  suggested_bid: M({
    label: 'Suggested bid', unit: 'money', group: 'diagnostics', totals: 'none', format: 'money', filter: 'money', point: true,
    formula: 'Amazon’s suggested bid for this target.',
  }),
  placement_modifier: M({
    label: 'Modifier', unit: 'percent', group: 'money', totals: 'none', format: 'percent', filter: 'percent', point: true,
    formula: 'The bid boost for this placement, in percent over the base bid.',
  }),
  proposed_modifier: M({
    label: 'Proposed modifier', unit: 'percent', group: 'money', totals: 'none', format: 'percent', filter: 'percent', point: true,
    formula: 'The placement bid boost the suggestion would set.',
  }),
  est_daily_spend_delta: M({
    label: 'Est. $/day impact', unit: 'money', group: 'money', totals: 'sum', format: 'money', filter: 'money', point: true,
    formula: 'The estimated change in daily ad spend if the suggestion is applied.',
  }),
  price: M({
    label: 'Price', unit: 'money', group: 'money', totals: 'none', format: 'money', filter: 'money', point: true,
    formula: 'The current selling price.',
  }),
  cogs: M({
    label: 'Cost of goods', unit: 'money', group: 'money', totals: 'sum', format: 'money', filter: 'money',
    formula: 'Landed cost of the units sold.',
  }),
  cogs_per_unit: M({
    label: 'Cost per unit', unit: 'money', group: 'money', totals: 'none', format: 'money', filter: 'money', point: true,
    formula: 'Cost of goods ÷ units that have a cost on file.',
  }),
  fees: M({
    label: 'Fees', unit: 'money', group: 'money', totals: 'sum', format: 'money', filter: 'money',
    formula: 'Marketplace fees (referral, fulfillment and other channel fees).',
  }),
  refunds: M({
    label: 'Refunds', unit: 'money', group: 'money', totals: 'sum', format: 'money', filter: 'money',
    formula: 'Money refunded to customers.',
  }),
  refund_rate: M({
    label: 'Refund %', unit: 'percent', group: 'money', totals: 'ratio', format: 'percent', filter: 'percent',
    ratio: { num: 'refund_units', den: 'units' },
    formula: 'Refunded units ÷ units sold.',
  }),
  roi: M({
    label: 'ROI', unit: 'percent', group: 'money', totals: 'ratio', format: 'percent', filter: 'percent',
    ratio: { num: 'net_profit', den: 'cogs' },
    formula: 'Net profit ÷ cost of goods.',
  }),
  est_payout: M({
    label: 'Est. payout', unit: 'money', group: 'money', totals: 'sum', format: 'money', filter: 'money',
    formula: 'What the marketplace is expected to pay out: sales less fees and refunds.',
  }),
  organic_sales: M({
    label: 'Organic sales', unit: 'money', group: 'money', totals: 'sum', format: 'money', filter: 'money',
    formula: 'Sales not attributed to an Amazon ad: sales − ad-attributed sales.',
  }),
  ad_share: M({
    label: 'Ad share', unit: 'percent', group: 'money', totals: 'ratio', format: 'percent', filter: 'percent',
    ratio: { num: 'ad_sales', den: 'sales' },
    formula: 'Ad-attributed sales ÷ all sales.',
  }),
  inventory_value: M({
    label: 'Inventory value ($)', unit: 'money', group: 'stock', totals: 'sum', format: 'money', filter: 'money', point: true,
    formula: 'Units you own × landed cost per unit. On-order stock is not included.',
  }),
  gross_profit: M({
    label: 'Gross profit', unit: 'money', group: 'money', totals: 'sum', format: 'money', filter: 'money',
    formula: 'Sales less cost of goods and fees, before ad spend.',
  }),
  aov: M({
    label: 'AOV', unit: 'money', group: 'money', totals: 'none', format: 'money', filter: 'money',
    formula: 'Average order value: sales ÷ orders.',
  }),
  revenue: M({
    label: 'Revenue', unit: 'money', group: 'money', totals: 'sum', format: 'money', filter: 'money',
    formula: 'What the customer paid for this order, in dollars.',
  }),
  promo: M({
    label: 'Promo', unit: 'money', group: 'money', totals: 'sum', format: 'money', filter: 'money',
    formula: 'Promotion discount given on this order, in dollars.',
  }),
  order_profit: M({
    label: 'Profit', unit: 'money', group: 'money', totals: 'sum', format: 'money', filter: 'money',
    formula: 'Revenue less cost of goods, fees and promo for this order.',
  }),

  // ── Efficiency ───────────────────────────────────────────────────────────
  cvr: M({
    label: 'CVR', unit: 'percent', group: 'efficiency', totals: 'ratio', format: 'percent', filter: 'percent',
    ratio: { num: 'orders', den: 'clicks' },
    formula: 'Orders ÷ clicks.',
    example: '3 orders ÷ 60 clicks = 5 %.',
  }),
  page_cvr: M({
    label: 'Page CVR', unit: 'percent', group: 'efficiency', totals: 'ratio', format: 'percent', filter: 'percent',
    ratio: { num: 'units', den: 'page_views' },
    formula: 'Units sold ÷ Amazon page views (page views, because Amazon’s sessions feed is not live yet).',
  }),
  ctr: M({
    label: 'CTR', unit: 'percent', group: 'efficiency', totals: 'ratio', format: 'percent', filter: 'percent',
    ratio: { num: 'clicks', den: 'impressions' },
    formula: 'Clicks ÷ impressions.',
  }),
  cpc: M({
    label: 'CPC', unit: 'money', group: 'efficiency', totals: 'ratio', format: 'money', filter: 'money',
    ratio: { num: 'spend', den: 'clicks' },
    formula: 'Cost per click: spend ÷ clicks.',
  }),
  roas: M({
    label: 'ROAS', unit: 'multiple', group: 'efficiency', totals: 'ratio', filter: 'number',
    ratio: { num: 'sales', den: 'spend' },
    formula: 'Return on ad spend: ad sales ÷ spend.',
  }),
  tos_share: M({
    label: 'Top of Search %', unit: 'percent', group: 'diagnostics', totals: 'none', format: 'percent', filter: 'percent',
    formula: 'Share of this campaign’s spend that went to Top of Search.',
  }),
  pp_share: M({
    label: 'Product pages %', unit: 'percent', group: 'diagnostics', totals: 'none', format: 'percent', filter: 'percent',
    formula: 'Share of this campaign’s spend that went to Product pages.',
  }),
  ros_share: M({
    label: 'Rest of Search %', unit: 'percent', group: 'diagnostics', totals: 'none', format: 'percent', filter: 'percent',
    formula: 'Share of this campaign’s spend that went to Rest of Search.',
  }),

  // ── Volume ───────────────────────────────────────────────────────────────
  clicks: M({
    label: 'Clicks', unit: 'int', group: 'volume', totals: 'sum', format: 'int', filter: 'number',
    formula: 'Ad clicks.',
  }),
  impressions: M({
    label: 'Impressions', unit: 'int', group: 'volume', totals: 'sum', format: 'int', filter: 'number',
    formula: 'Times an ad was shown.',
  }),
  units_sold: M({
    label: 'Units sold', unit: 'units', group: 'volume', totals: 'sum', format: 'int', filter: 'number',
    formula: 'Units sold.',
  }),
  units_per_day: M({
    label: 'Units/day', unit: 'per_day', group: 'volume', totals: 'sum', filter: 'number',
    formula: 'Units sold per day (units ÷ days in the window).',
  }),
  page_views: M({
    label: 'Page views', unit: 'int', group: 'volume', totals: 'sum', format: 'int', filter: 'number',
    formula: 'Amazon page views, the bottom of Page CVR.',
  }),
  sessions: M({
    label: 'Sessions', unit: 'int', group: 'volume', totals: 'sum', format: 'int', filter: 'number',
    formula: 'Amazon sessions: visits to the listing.',
  }),
  ad_clicks: M({
    label: 'Ad clicks', unit: 'int', group: 'volume', totals: 'sum', format: 'int', filter: 'number',
    formula: 'Sponsored Products and Display clicks.',
  }),
  ad_impressions: M({
    label: 'Ad impressions', unit: 'int', group: 'volume', totals: 'sum', format: 'int', filter: 'number',
    formula: 'Sponsored Products and Display impressions.',
  }),
  cancelled_orders: M({
    label: 'Cancelled', unit: 'int', group: 'volume', totals: 'sum', format: 'int', filter: 'number',
    formula: 'Orders that were cancelled; never counted in Orders, Units or Sales. Blank means the orders feed cannot answer, not zero.',
  }),
  order_units: M({
    label: 'Units (order)', unit: 'units', group: 'volume', totals: 'sum', format: 'int', filter: 'number',
    formula: 'Units in this order.',
  }),

  // ── Stock ────────────────────────────────────────────────────────────────
  stock_units: M({
    label: 'Stock (units)', unit: 'units', group: 'stock', totals: 'sum', format: 'int', filter: 'number', point: true,
    formula: 'Units on hand.',
  }),
  stock_dos: M({
    label: 'Stock (DOS)', unit: 'units', group: 'stock', totals: 'sum', point: true,
    formula: 'Units we own (warehouse + other warehouses + Walmart WFS + FBA on hand + FBA inbound) and how many days they last at the current sales rate (days of supply). On an Amazon marketplace the days are that marketplace’s FBA stock ÷ its own sales. On-order stock is not included.',
  }),
  days_of_cover: M({
    label: 'Days of cover', unit: 'days', group: 'stock', totals: 'none', filter: 'number', point: true,
    formula: 'Days the stock lasts at the current sales rate.',
    example: '1,000 units ÷ 10 a day = 100 days.',
  }),
  fba_stock: M({
    label: 'FBA stock (units)', unit: 'units', group: 'stock', totals: 'sum', format: 'int', filter: 'number', point: true,
    formula: `${FBA_STOCK_DEFINITION} Amazon US unless the page is on CA.`,
  }),
  fba_qty_rules: M({
    label: 'FBA units incl. inbound (rules)', unit: 'units', group: 'stock', totals: 'sum', format: 'int', filter: 'number', point: true,
    formula: 'FBA stock plus inbound. This is the number the automation rules read, so a rule that filters on FBA units counts inbound too.',
  }),
  inbound: M({
    label: 'FBA inbound (units)', unit: 'units', group: 'stock', totals: 'sum', format: 'int', filter: 'number', point: true,
    formula: 'Units on their way into Amazon warehouses. Not part of FBA stock.',
  }),
  on_order: M({
    label: 'On order (units)', unit: 'units', group: 'stock', totals: 'sum', format: 'int', filter: 'number', point: true,
    formula: 'Units the supplier still owes on purchase orders not yet received or cancelled.',
  }),
  warehouse: M({
    label: 'Warehouse (units)', unit: 'units', group: 'stock', totals: 'sum', format: 'int', filter: 'number', point: true,
    formula: 'Main (Default) warehouse only. Other warehouses are in Total stock.',
  }),
  wfs: M({
    label: 'Walmart WFS (units)', unit: 'units', group: 'stock', totals: 'sum', format: 'int', filter: 'number', point: true,
    formula: 'Units in Walmart Fulfillment Services warehouses.',
  }),

  // ── Diagnostics ──────────────────────────────────────────────────────────
  buy_box_pct: M({
    label: 'Buy Box %', unit: 'percent', group: 'diagnostics', totals: 'none', format: 'percent', filter: 'percent',
    formula: 'Share of page views where we held the Buy Box.',
  }),
  buy_box_price: M({
    label: 'Buy Box price', unit: 'money', group: 'diagnostics', totals: 'none', format: 'money', filter: 'money', point: true,
    formula: 'The price currently winning the Buy Box.',
  }),
  rating: M({
    label: 'Rating', unit: 'text', group: 'diagnostics', totals: 'none', filter: 'number', point: true,
    formula: 'Amazon star rating.',
  }),
  reviews: M({
    label: 'Reviews', unit: 'int', group: 'diagnostics', totals: 'none', format: 'int', filter: 'number', point: true,
    formula: 'Number of Amazon reviews.',
  }),
  bsr: M({
    label: 'BSR', unit: 'int', group: 'diagnostics', totals: 'none', filter: 'number', point: true,
    formula: 'Amazon Best Sellers Rank (lower is better).',
  }),
  rules: M({
    label: 'Rules', unit: 'int', group: 'diagnostics', totals: 'none', format: 'int', filter: 'number', point: true,
    formula: 'Automation rules acting on this campaign.',
  }),
  confidence: M({
    label: 'Confidence', unit: 'text', group: 'status', totals: 'none', point: true,
    formula: 'How sure ClickPay is about this suggestion.',
  }),
  audience_size: M({
    label: 'Audience size', unit: 'int', group: 'diagnostics', totals: 'none', format: 'int', filter: 'number', point: true,
    formula: 'People Amazon estimates this audience reaches.',
  }),
  best_hour: M({
    label: 'Best hour', unit: 'text', group: 'diagnostics', totals: 'none', point: true,
    formula: 'The hour of the day this converts best.',
  }),
  worst_hour: M({
    label: 'Worst hour', unit: 'text', group: 'diagnostics', totals: 'none', point: true,
    formula: 'The hour of the day this converts worst.',
  }),
  first_seen: M({
    label: 'First seen', unit: 'date', group: 'diagnostics', totals: 'none', point: true,
    formula: 'When ClickPay first saw this.',
  }),
} satisfies Record<string, MetricEntry>

export type MetricId = keyof typeof METRICS

/** The explicit identity / text exemptions: a label here is a name, a state, a
 *  date or a free-text column, not a number. Capitalisation is the standard:
 *  first word only ("Ad group", "Search term"). */
export const IDENTITY_TEXT_LABELS: ReadonlySet<string> = new Set([
  '', 'Action', 'Campaign', 'Ad group', 'Portfolio', 'ASIN', 'SKU', 'UID', 'Product', 'Keyword', 'Target',
  'Search term', 'Placement', 'Negative keyword', 'Blocked product', 'Match', 'Type', 'Level', 'State',
  'Status', 'Coverage', 'Tags', 'Marketplace', 'Marketplaces', 'State mix', 'Brand', 'PM', 'Goal', 'Signal',
  'Campaign ID', 'Ad group ID', 'Negative ID', 'Profile ID', 'Campaign state', 'Suggestion', 'Category',
  'Keyword / Target', 'Audience', 'Recipe', 'Current → Proposed', 'Suggested hours', 'Last changed', 'Group',
  'Products', 'Hour', 'Title', 'Docs', 'Problem', 'Runway', 'Send qty', 'Order ID', 'Listing ID', 'Date',
  'Lines', 'Supplier', 'PO date',
  // PR 18's ops Orders / Shipments / COGS set -- they carry their own unit.
  'FOB $ / unit', 'Freight $ / unit', 'Duty $ / unit', 'Landed $ / unit', 'Carton L × W × H (cm)',
  'Gross kg / carton', 'CBM / carton', 'Units / carton', 'Sale price', 'Balance due now', 'Balance due date',
])

/** The one label two entries may share (OD-7): ad-attributed vs every order. */
export const SHARED_LABELS: ReadonlySet<string> = new Set(['Orders'])

const WINDOW_SUFFIX: Record<Exclude<MetricWindow, 'today'>, string> = { '7d': '7d', '30d': '30d', '90d': '90d', '12mo': '12 mo' }
const WINDOWS: (MetricWindow | undefined)[] = [undefined, 'today', '7d', '30d', '90d', '12mo']

/** "Today " prefix keeps acronyms ("Today ACoS") and lower-cases words ("Today ad sales"). */
function lowerFirstWord(label: string): string {
  const second = label.charAt(1)
  return second && second === second.toUpperCase() && second !== second.toLowerCase() ? label : label.charAt(0).toLowerCase() + label.slice(1)
}

/** 'Spend' | 'Spend (7d)' | 'Spend (30d)' | 'Units sold (12 mo)' | 'Today spend'. */
export function metricLabel(id: MetricId, window?: MetricWindow): string {
  const label = METRICS[id].label
  if (!window) return label
  if (window === 'today') return `Today ${lowerFirstWord(label)}`
  return `${label} (${WINDOW_SUFFIX[window]})`
}

const WINDOW_SENTENCE: Record<MetricWindow, string> = {
  today: 'Today so far (preliminary).',
  '7d': 'Last 7 days.',
  '30d': 'Last 30 days.',
  '90d': 'Last 90 days.',
  '12mo': 'Last 12 months.',
}

/** The formula, the worked example, then the window sentence. */
export function metricTitle(id: MetricId, window?: MetricWindow): string {
  const e: MetricEntry = METRICS[id]
  const parts = [e.formula]
  if (e.example) parts.push(`Example: ${e.example}`)
  if (window) parts.push(WINDOW_SENTENCE[window])
  else if (!e.point && (e.group === 'money' || e.group === 'efficiency' || e.group === 'volume')) parts.push('Over the selected date range.')
  return parts.join(' ')
}

type MetricColFields<Row> = Pick<
  ColumnDef<Row>,
  'label' | 'title' | 'format' | 'align' | 'totalsAgg' | 'totalsNum' | 'totalsDen' | 'filter' | 'group' | 'numeric'
>

const RIGHT_UNITS: ReadonlySet<MetricUnit> = new Set(['money', 'percent', 'int', 'days', 'multiple', 'per_day', 'units'])

/**
 * The ColumnDef fields one glossary entry supplies. Spread it, then add the
 * page's own `key`, `sortable`, `render`. `opts.title` REPLACES the formula only
 * for the same metric with a different source (Restock's days of cover);
 * label and unit never change. `opts.totals` overrides the entry's ratio (or
 * 'none' switches totals off) for a page whose row fields differ.
 */
export function metricCol<Row>(
  id: MetricId,
  opts: { window?: MetricWindow; totals?: MetricEntry['ratio'] | 'none'; title?: string } = {},
): MetricColFields<Row> {
  const e: MetricEntry = METRICS[id]
  const out: MetricColFields<Row> = {
    label: metricLabel(id, opts.window),
    title: opts.title ?? metricTitle(id, opts.window),
    group: e.group,
  }
  if (e.format) out.format = e.format
  if (RIGHT_UNITS.has(e.unit)) out.align = 'right'
  if (e.filter) out.filter = { type: e.filter }
  if (e.unit !== 'text' && e.unit !== 'date') out.numeric = true
  const totals = opts.totals
  if (totals === 'none') out.totalsAgg = 'none'
  else if (totals) {
    out.totalsAgg = 'ratio'
    out.totalsNum = totals.num
    out.totalsDen = totals.den
  } else if (e.totals === 'ratio' && e.ratio) {
    out.totalsAgg = 'ratio'
    out.totalsNum = e.ratio.num
    out.totalsDen = e.ratio.den
  } else {
    out.totalsAgg = e.totals === 'sum' ? 'sum' : 'none'
  }
  return out
}

/** Every id whose base label is `label` (the shared "Orders" gives two). */
export function metricIdsByLabel(label: string): MetricId[] {
  return (Object.keys(METRICS) as MetricId[]).filter((id) => METRICS[id].label === label)
}

/** Every label form a column may carry -- what the source scan matches. */
export function allMetricLabels(): Set<string> {
  const out = new Set<string>()
  for (const id of Object.keys(METRICS) as MetricId[]) for (const w of WINDOWS) out.add(metricLabel(id, w))
  return out
}
